/**
 * Asset Manufacturer Hooks
 * ========================
 * React Query hooks for IT asset manufacturer operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { assetManufacturerApi } from '@/services/api'
import type { AssetManufacturerCreate, AssetManufacturerUpdate } from '@/types'

// Query keys
export const assetManufacturerKeys = {
  all: ['assetManufacturers'] as const,
  lists: () => [...assetManufacturerKeys.all, 'list'] as const,
  list: () => [...assetManufacturerKeys.lists()] as const,
  details: () => [...assetManufacturerKeys.all, 'detail'] as const,
  detail: (id: number) => [...assetManufacturerKeys.details(), id] as const,
}

/**
 * Hook to fetch all asset manufacturers with asset counts.
 */
export function useAssetManufacturers() {
  return useQuery({
    queryKey: assetManufacturerKeys.list(),
    queryFn: () => assetManufacturerApi.list(),
  })
}

/**
 * Hook to fetch a single asset manufacturer.
 */
export function useAssetManufacturer(id: number) {
  return useQuery({
    queryKey: assetManufacturerKeys.detail(id),
    queryFn: () => assetManufacturerApi.get(id),
    enabled: !!id,
  })
}

/**
 * Hook to create a new asset manufacturer.
 */
export function useCreateAssetManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: AssetManufacturerCreate) => assetManufacturerApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetManufacturerKeys.lists() })
    },
  })
}

/**
 * Hook to update an asset manufacturer.
 */
export function useUpdateAssetManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AssetManufacturerUpdate }) =>
      assetManufacturerApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: assetManufacturerKeys.lists() })
      queryClient.invalidateQueries({ queryKey: assetManufacturerKeys.detail(id) })
    },
  })
}

/**
 * Hook to delete an asset manufacturer.
 */
export function useDeleteAssetManufacturer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => assetManufacturerApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetManufacturerKeys.lists() })
    },
  })
}
